/**
 * 工资条清单（**一项一行**）—— 批 `141` 的前端主体。
 *
 * 依据：`DR-016` §3（结构 / 自由度 / 录入期对账锚点）· C 域 PRD §5.2.1（十条铁律）
 * · 设计原型 `docs/03-UX与UI设计/18-工资条重构_原型.html`
 *
 * ## 🔴 与改前的差别（一句话）
 *
 * 改前每个子项是一张**卡片**，卡片里 6 格：方向 / 账户 / 金额 / 优惠 / 分类 / 不计收支
 * （7 项 ≈ 42 个控件 + 十几段说明）。改后是**一行一项**，每行 3 个触点：
 * **名称 · 金额 · 去向**；方向 / 账户 / 分类 / 不计收支 四件事收进「去向」的展开区。
 *
 * ## 🔴 这个组件**不判任何账务**
 *
 * | 事 | 谁做 |
 * |---|---|
 * | 项 → 腿 | `features/salary-items.ts` 的 `expandSalaryItems()`（唯一展开处） |
 * | 腿 → 请求 / 形态校验 | `features/txn-draft.ts` 的 `buildRequest()` + 共享层 `checkLegShape()` |
 * | 必填校验 | `validateSalaryItems()`（**只报用户能改的事**） |
 * | 本组件 | 排版 + 受控输入 + 展开哪一项 |
 *
 * ## 🔴 「到账金额」不落库（`DR-016` §3.8⑤）
 *
 * 它只用来做**录入期对账**：实发 = Σ子项（代数和）。差额**只提示、不阻断** ——
 * 父项是派生值，记录本身不会错；差额只说明"还有没归类的东西"。
 */

import { useState, type ReactElement } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CategoryKind,
  currencySymbolOf,
  formatMoneyText,
  type CategoryView,
  type Cents,
} from '@jiaying/shared';

import { AccountPicker, type AccountPickerItem } from './AccountPicker.js';
import { CategoryPicker } from './CategoryPicker.js';
import {
  emptySalaryItem,
  salaryTotals,
  withType,
  type SalaryItem,
  type SalaryItemGroup,
  type SalaryItemType,
} from '../features/salary-items.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';

export interface SalarySheetProps {
  readonly items: readonly SalaryItem[];
  readonly onItemsChange: (next: SalaryItem[]) => void;
  /** 工资到账的账户（**资金出口唯一** · `DR-016` §3.8③） */
  readonly mainAccountId: string;
  readonly onMainAccountChange: (id: string) => void;
  /** 到账账户候选 = 日常账户（`dailyAccountsOf()` 的结果，顺序即展示顺序） */
  readonly mainAccounts: readonly AccountPickerItem[];
  /** 转账目标候选 = **全量账户**（社保 / 贷款 / 投资都在内） */
  readonly targetAccounts: readonly AccountPickerItem[];
  readonly categories: readonly CategoryView[];
  /** 银行到账金额（**只用于对账，不提交** —— `DR-016` §3.8⑤） */
  readonly netText: string;
  readonly onNetTextChange: (text: string) => void;
  /**
   * 🆕 **「沿用上月」**（批 141）。不传 ⇒ 不渲染按钮（组件自己不发请求 ——
   * 取数在页面层，本组件只负责"有没有这个入口"）。
   */
  readonly onCopyLast?: () => void;
  readonly copyBusy?: boolean;
  readonly disabled?: boolean;
}

const GROUPS: readonly { key: SalaryItemGroup; title: string; hint: string }[] = [
  { key: 'gross', title: '应发', hint: '工资条上「应发合计」那几项' },
  { key: 'deduct', title: '代扣', hint: '' },
  { key: 'after', title: '税后去向', hint: '到手的钱里先安排出去的（可不填）' },
];

/** 类型的中文名（`DR-016` §3.8② 的三选一） */
const TYPE_LABEL: Record<SalaryItemType, string> = {
  income: '收入',
  expense: '支出',
  transfer: '转账',
};

export function SalarySheet(props: SalarySheetProps): ReactElement {
  const [openId, setOpenId] = useState<string | null>(null);

  const totals = salaryTotals(props.items);
  const netCents = yuanTextToCentsOrNull(props.netText);
  const netCurrency = resolveCurrency(props);
  const sym = netCurrency === '' ? '' : currencySymbolOf(netCurrency);
  const gapCents = netCents === null ? 0 : netCents - totals.netCents;
  const matched = netCents !== null && gapCents === 0;

  const patch = (id: string, next: Partial<SalaryItem>): void => {
    props.onItemsChange(props.items.map((it) => (it.id === id ? { ...it, ...next } : it)));
  };
  const remove = (id: string): void => {
    props.onItemsChange(props.items.filter((it) => it.id !== id));
    if (openId === id) setOpenId(null);
  };
  const add = (group: SalaryItemGroup): void => {
    const id = `si-${String(Date.now())}-${String(props.items.length)}`;
    const next = emptySalaryItem(id, group);
    props.onItemsChange([...props.items, next]);
    setOpenId(id);
  };

  return (
    <Box data-testid="salary-sheet">
      {/* ── 到账卡：全页唯一的大数字 ── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 2,
          pb: 2,
          mb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              这个月到手（实发）
            </Typography>
            <Chip
              size="small"
              data-testid="salary-badge"
              color={netCents === null ? 'default' : matched ? 'success' : 'warning'}
              variant={netCents === null ? 'outlined' : 'filled'}
              label={
                netCents === null
                  ? '填一个到账金额来对账'
                  : matched
                    ? '与清单一致'
                    : `差 ${formatMoneyText(Math.abs(gapCents) as Cents, netCurrency)}${gapCents > 0 ? ' 未归类' : ' 多算了'}`
              }
            />
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="h5" color="text.secondary">
              {sym === '' ? '¥' : sym}
            </Typography>
            <TextField
              variant="standard"
              value={props.netText}
              onChange={(e) => props.onNetTextChange(e.target.value)}
              placeholder="0.00"
              disabled={props.disabled}
              slotProps={{
                htmlInput: {
                  'data-testid': 'salary-net',
                  inputMode: 'decimal',
                  'aria-label': '实发到账金额',
                  style: { fontVariantNumeric: 'tabular-nums', fontSize: 28, fontWeight: 700 },
                },
              }}
              sx={{ width: 200 }}
            />
          </Stack>
        </Box>
        <Box sx={{ minWidth: 250, flex: '1 1 220px', maxWidth: 320 }}>
          <AccountPicker
            label="到账账户"
            accounts={props.mainAccounts}
            value={props.mainAccountId}
            onChange={props.onMainAccountChange}
            size="small"
            testId="salary-main-account"
            helperText="整笔工资都从这张卡进出"
            disabled={props.disabled}
          />
        </Box>
      </Box>

      {/* ── 清单 ── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          工资条清单
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {String(totals.usedCount)} 项
        </Typography>
        {props.onCopyLast !== undefined && (
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <Chip
              size="small"
              clickable
              variant="outlined"
              onClick={props.onCopyLast}
              disabled={props.copyBusy === true || props.disabled === true}
              data-testid="salary-copy-last"
              label={props.copyBusy === true ? '读取上月…' : '沿用上月'}
            />
          </Box>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        已按常见工资条填好 —— 不对的删掉、金额改成你的，就完事。
      </Typography>

      {GROUPS.map((group) => {
        const rows = props.items.filter((it) => it.group === group.key);
        const sum = sumOf(rows);
        return (
          <Box key={group.key} data-testid={`salary-group-${group.key}`}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ letterSpacing: '.08em', fontWeight: 600 }}
              >
                {group.title}
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {sum === 0 ? '—' : `${group.key === 'gross' ? '+' : '−'}${formatMoneyText(sum, netCurrency)}`}
              </Typography>
              <IconButton
                size="small"
                aria-label={`往「${group.title}」加一项`}
                onClick={() => add(group.key)}
                disabled={props.disabled}
                data-testid={`salary-add-${group.key}`}
              >
                ＋
              </IconButton>
            </Stack>

            {rows.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5, pb: 1 }}>
                {group.key === 'after' ? '还没有 —— 多数人这里不用填' : '还没有'}
              </Typography>
            )}

            {rows.map((item) => {
              const open = openId === item.id;
              return (
                <Box key={item.id}>
                  <Box
                    data-testid={`salary-row-${item.id}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 120px auto 32px',
                      alignItems: 'center',
                      columnGap: 1,
                      minHeight: 44,
                      borderRadius: 1,
                      bgcolor: open ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TextField
                      variant="standard"
                      value={item.name}
                      onChange={(e) => patch(item.id, { name: e.target.value })}
                      disabled={props.disabled}
                      slotProps={{
                        htmlInput: {
                          'data-testid': `salary-name-${item.id}`,
                          'aria-label': '子项名称',
                          style: { fontSize: 13 },
                        },
                      }}
                      sx={{ ml: 0.5 }}
                    />
                    <TextField
                      variant="standard"
                      value={item.amountText}
                      onChange={(e) => patch(item.id, { amountText: e.target.value })}
                      placeholder="0.00"
                      disabled={props.disabled}
                      slotProps={{
                        htmlInput: {
                          'data-testid': `salary-amount-${item.id}`,
                          inputMode: 'decimal',
                          'aria-label': `${item.name} 金额`,
                          style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
                        },
                      }}
                    />
                    <Chip
                      size="small"
                      clickable
                      variant="outlined"
                      onClick={() => setOpenId(open ? null : item.id)}
                      data-testid={`salary-dest-${item.id}`}
                      label={destLabel(item, props)}
                      sx={{ maxWidth: 150 }}
                    />
                    <IconButton
                      size="small"
                      aria-label={`删除「${item.name}」`}
                      onClick={() => remove(item.id)}
                      disabled={props.disabled}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      ✕
                    </IconButton>
                  </Box>

                  {open && (
                    <Box
                      data-testid={`salary-edit-${item.id}`}
                      sx={{
                        ml: 1.5,
                        mb: 1,
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        borderLeft: 2,
                        borderLeftColor: 'primary.main',
                        borderRadius: '0 8px 8px 0',
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            这一项是
                          </Typography>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={item.type}
                            onChange={(_e, v: SalaryItemType | null) => {
                              if (v === null) return;
                              // 🔴 换类型必须清掉另一条通路的去向（见 `withType` 的说明）
                              props.onItemsChange(
                                props.items.map((it) => (it.id === item.id ? withType(it, v) : it)),
                              );
                            }}
                          >
                            {(['income', 'expense', 'transfer'] as const).map((t) => (
                              <ToggleButton key={t} value={t} data-testid={`salary-type-${t}`}>
                                {TYPE_LABEL[t]}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </Box>

                        {item.type === 'transfer' ? (
                          <AccountPicker
                            label="转到哪个账户"
                            accounts={props.targetAccounts}
                            value={item.targetAccountId ?? ''}
                            onChange={(id) => patch(item.id, { targetAccountId: id })}
                            size="small"
                            testId={`salary-target-${item.id}`}
                            allowEmpty
                            helperText="社保 / 贷款 / 投资账户都在这里；钱只是换了个口袋"
                          />
                        ) : (
                          <CategoryPicker
                            kind={item.type === 'income' ? CategoryKind.Income : CategoryKind.Expense}
                            items={props.categories}
                            value={item.categoryId}
                            onChange={(v) => patch(item.id, { categoryId: v })}
                            label="记到哪个分类"
                            showHint={false}
                          />
                        )}

                        <Typography variant="caption" color="text.secondary">
                          {item.type === 'transfer'
                            ? '不计收支：钱转进自己的账户，净资产不变'
                            : '计入收支：这一项会出现在收支报表里'}
                          {item.type !== 'transfer' && (
                            <Box component="span" sx={{ ml: 1 }}>
                              <Box
                                component="button"
                                type="button"
                                data-testid={`salary-override-${item.id}`}
                                onClick={() =>
                                  patch(item.id, {
                                    excludeFromStats: item.excludeFromStats === true ? undefined : true,
                                  })
                                }
                                sx={{
                                  border: 0,
                                  background: 'none',
                                  p: 0,
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  font: 'inherit',
                                  textDecoration: 'underline',
                                }}
                              >
                                {item.excludeFromStats === true ? '改回计入' : '改为不计收支'}
                              </Box>
                            </Box>
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}

      <Divider sx={{ mt: 1.5 }} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 1.5, pb: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          应发 {formatMoneyText(totals.grossCents, netCurrency)}
          {totals.deductCents > 0 ? ` − 代扣 ${formatMoneyText(totals.deductCents, netCurrency)}` : ''}
          {totals.afterCents > 0 ? ` − 去向 ${formatMoneyText(totals.afterCents, netCurrency)}` : ''}
          {' = '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            实发 {formatMoneyText(totals.netCents, netCurrency)}
          </Box>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" data-testid="salary-gap">
          {netCents === null
            ? '差额不阻断保存'
            : matched
              ? '与银行到账一致'
              : `差 ${formatMoneyText(Math.abs(gapCents) as Cents, netCurrency)}（可补一项「其他扣减」）`}
        </Typography>
      </Stack>
    </Box>
  );
}

/** 组小计（只算有金额的项；与 `salaryTotals` 同一取舍） */
function sumOf(rows: readonly SalaryItem[]): Cents {
  let sum = 0;
  for (const row of rows) {
    const cents = yuanTextToCentsOrNull(row.amountText);
    if (cents !== null && cents > 0) sum += cents;
  }
  return sum as Cents;
}

/** 去向的展示文案（**转账带箭头**，一眼看出"钱去哪了"） */
function destLabel(item: SalaryItem, props: SalarySheetProps): string {
  if (item.type === 'transfer') {
    const acc = props.targetAccounts.find((a) => a.id === item.targetAccountId);
    return `→ ${acc === undefined ? '选择账户' : acc.name}`;
  }
  const cat = props.categories.find((c) => c.id === item.categoryId);
  return cat === undefined ? '选择分类' : cat.name;
}

/** 本笔的币种 = 到账账户的币种（多币种工资属 P1，这里只做展示） */
function resolveCurrency(props: SalarySheetProps): string {
  return props.mainAccounts.find((a) => a.id === props.mainAccountId)?.currency ?? 'CNY';
}
